import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [label, setLabel] = useState("your purchase");
  const [lookup, setLookup] = useState(null);
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setLabel(data.label || "your purchase");
        setLookup(data.lookup_key);
        if (data.payment_status === "paid") {
          setState("paid");
          checkAuth();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setState("error");
          return;
        }
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="panel p-10 max-w-md w-full text-center" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-smasher-gold animate-spin mx-auto mb-4" />
            <h1 className="font-heading font-black text-2xl text-white">Confirming payment…</h1>
            <p className="text-slate-400 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-heading font-black text-3xl text-gradient-gold">You're in!</h1>
            <p className="text-slate-300 mt-2">Payment confirmed for <span className="text-smasher-gold font-bold">{label}</span>.</p>
            <Link to={lookup === "ebook_football" ? "/ebook" : "/members"} className="btn-gold inline-block mt-6 px-6 py-3 rounded-xl font-heading font-black" data-testid="payment-success-continue">
              {lookup === "ebook_football" ? "DOWNLOAD YOUR EBOOK" : "GO TO MEMBERS AREA"}
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-14 h-14 text-smasher-gold mx-auto mb-4" />
            <h1 className="font-heading font-black text-2xl text-white">Still processing</h1>
            <p className="text-slate-400 mt-2 text-sm">Your payment is being finalized. You'll get access shortly.</p>
            <Link to="/" className="btn-ghost-gold inline-block mt-6 px-6 py-3 rounded-xl font-bold">BACK TO HOME</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading font-black text-2xl text-white">Something went wrong</h1>
            <p className="text-slate-400 mt-2 text-sm">We couldn't confirm this payment. If you were charged, contact support.</p>
            <Link to="/membership" className="btn-gold inline-block mt-6 px-6 py-3 rounded-xl font-heading font-black">TRY AGAIN</Link>
          </>
        )}
      </div>
    </div>
  );
}
